/**
 * Utils/fetchUserData.js
 *
 * Fetcher + formatter for the UserAccount page.
 * URL pattern:
 *   /user-account/{suserid}/{tuserid}
 * Mock file:
 *   /api/mock/userAccount/UserAccountPageMockData-{suserid}-{tuserid}.json
 *   /api/mock/userAccount/UserAccountPageMockData-{tuserid}.json          (tuserid-only)
 */

import UserPostStructure from "../Constants/UserPostStructure";

// ─── Base ────────────────────────────────────────────────────────────────────
const BASE = "/api/mock/userAccount";

// ─── Internal helpers ────────────────────────────────────────────────────────

function toArray(value) {
  if (Array.isArray(value)) return value;
  if (value == null) return [];
  return [value];
}

async function fetchJSON(url) {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(
      `Failed to fetch ${url}: ${response.status} ${response.statusText}`
    );
  }
  return response.json();
}

/**
 * Some backends wrap the payload in { data }, { user }, { result }, etc.
 * Strip one level of wrapping if present so formatUserData always sees the
 * root fields directly.
 */
function unwrap(raw) {
  if (!raw || typeof raw !== "object") return raw;
  const wrapperKeys = ["data", "user", "result", "payload", "response", "body"];
  for (const key of wrapperKeys) {
    if (raw[key] && typeof raw[key] === "object" && !Array.isArray(raw[key])) {
      // Heuristic: only unwrap if the inner object has at least one known
      // root field. Otherwise it's a coincidence.
      const inner = raw[key];
      const looksLikeRoot =
        "userId" in inner ||
        "nfriends" in inner ||
        "feeds" in inner ||
        "advertises" in inner ||
        "profileImage" in inner;
      if (looksLikeRoot) return inner;
    }
  }
  return raw;
}

// ─── Media matcher ──────────────────────────────────────────────────────────

/**
 * Normalises the many shapes media "type" can take:
 *   "image"            bare token
 *   "image/jpeg"       MIME string
 *   "video/reel"       custom subtype for reels
 *   "reel"             bare reel token
 */
function mediaMatches(mediatype, normalisedTarget) {
  if (!mediatype || typeof mediatype !== "string") return false;
  const mt = mediatype.toLowerCase().trim();

  switch (normalisedTarget) {
    case "images":
      // accept "image" or anything starting with "image/"
      return mt === "image" || mt.startsWith("image/");
    case "reels":
      // accept "reel", "video/reel", or anything starting with "reel/"
      return (
        mt === "reel" ||
        mt === "video/reel" ||
        mt.startsWith("reel/") ||
        mt.endsWith("/reel")
      );
    case "videos":
      // accept "video" or anything starting with "video/"
      return mt === "video" || mt.startsWith("video/");
    default:
      return mt === normalisedTarget;
  }
}

export function extractMediaByType(formattedData, contentType) {
  if (!formattedData || !Array.isArray(formattedData.feeds)) return [];

  const target = (contentType || "").toLowerCase();

  const results = [];
  // Monotonic counter guarantees uniqueness even if URL-based fallbacks collide
  // (e.g. several media items pointing at the same CDN URL).
  let fallbackCounter = 0;

  for (const feed of formattedData.feeds) {
    const medias = Array.isArray(feed.medias) ? feed.medias : [];
    for (const media of medias) {
      if (!mediaMatches(media.mediatype, target)) continue;

      const url = media.mediaurl ?? media.imageurl ?? null;

      const stableId =
        media.mediaId ??
        media.id ??
        (url ? `media:${url}` : null);

      results.push({
        id:
          stableId ??
          `media-fallback-${fallbackCounter++}`,
        mediaurl:    url,
        imageurl:    url,
        description: media.descriptions ?? media.description ?? null,
        posted_by:   feed.userId ?? media.uploadedBy ?? "Anonymous",
        user_avatar: feed.userimage ?? null,
        timestamp:   feed.timestamp ?? null,
        location:    feed.location ?? "",
        impressions: media.nlikes ?? 0,
        mediatype:   media.mediatype ?? null,
      });
    }
  }
  return results;
}


// ─── Formatter ──────────────────────────────────────────────────────────────

export function formatUserData(rawInput) {
  const raw = unwrap(rawInput) || {};

  // ── Advertises ─────────────────────────────────────────────────────────────
  const advertises = toArray(raw.advertises).map((ad) => ({
    advertiseId:       ad.advertiseId       ?? "",
    advertiseUUID:     ad.advertiseUUID     ?? "",
    profileId:         ad.profileId         ?? "",
    advertiseImageUrl: ad.advertiseImageUrl ?? "",
    PlannedDate:       ad.PlannedDate       ?? "",
    postedBy:          ad.postedBy          ?? "",
    postedOn:          ad.postedOn          ?? "",
  }));

  // ── Feeds ──────────────────────────────────────────────────────────────────
  const feeds = toArray(raw.feeds).map((feed) => {
    const medias = toArray(feed.medias).map((media) => ({
      mediatype: media.mediatype ?? "",
      mediaurl:  media.mediaurl  ?? media.imageurl ?? "",
      nlikes:    media.nlikes    ?? 0,
      comments:  toArray(media.comments).map((c) => ({
        userId:    c.userId    ?? "",
        comment:   c.comment   ?? "",
        timestamp: c.timestamp ?? "",
        location:  c.location  ?? "",
      })),
      nshares:   media.nshares  ?? 0,
      nsuggests: media.nsuggests ?? 0,
    }));

    return {
      postId:       feed.postId       ?? "",
      userId:       feed.userId       ?? "",
      useruuid:     feed.useruuid     ?? "",
      userimage:    feed.userimage    ?? "",
      descriptions: feed.descriptions ?? "",
      timestamp:    feed.timestamp    ?? "",
      location:     feed.location     ?? "",
      nlikes:       feed.nlikes       ?? 0,
      medias,
      comments: toArray(feed.comments).map((c) => ({
        userId:    c.userId    ?? "",
        comment:   c.comment   ?? "",
        timestamp: c.timestamp ?? "",
        location:  c.location  ?? "",
      })),
      nshares: feed.nshares ?? 0,
    };
  });

  // ── Root ───────────────────────────────────────────────────────────────────
  return {
    ...UserPostStructure,
    userId:         raw.userId         ?? "",
    nfriends:       raw.nfriends       ?? 0,
    activeLevel:    raw.activeLevel    ?? "",
    profileImage:   raw.profileImage   ?? "",
    lastUpdatedOn:  raw.lastUpdatedOn  ?? "",
    location:       raw.location       ?? "",
    createdOn:      raw.createdOn      ?? "",
    lastActive:     raw.lastActive     ?? "",
    advertises,
    feeds,
  };
}

// ─── Public fetchers ────────────────────────────────────────────────────────

export async function fetchUserData(suserid, tuserid) {
  const segment = `${encodeURIComponent(suserid)}-${encodeURIComponent(tuserid)}`;
  const url = `${BASE}/UserAccountPageMockData-${segment}.json`;
  console.log("[fetchUserData] GET →", url);
  const raw = await fetchJSON(url);
  return formatUserData(raw);
}

export async function fetchUserByIdOnly(tuserid) {
  const segment = encodeURIComponent(tuserid);
  const url = `${BASE}/UserAccountPageMockData-${segment}.json`;
  console.log("[fetchUserData] GET →", url);
  const raw = await fetchJSON(url);
  return formatUserData(raw);
}

// ─── Default export (convenience object) ─────────────────────────────────────
export default {
  fetchUserData,
  fetchUserByIdOnly,
  extractMediaByType,
  formatUserData,
};
